import { useCallback, useContext, useMemo } from 'react';
import useApiCall from 'hooks/use-api-call';
import useInvestorMe from 'hooks/use-investor-me';
import { AdditionalInformationFields } from 'libraries/wizard/components/additional-information/additional-information-form';
import useInvestmentInvitation from 'subapps/investment/pages/investment/hooks/use-investment-invitation';
import { CurrencyEnum, InvestmentsApi } from 'api';
import WizardContext from 'libraries/wizard/wizard-context';
import { AnyServerError } from 'hooks/use-api-error';
import useInvestment from 'hooks/use-investment';
import { toMoney } from 'ui/helper/money';

const useAdditionalInformation = (): {
  onSubmit: (values: AdditionalInformationFields) => void;
  bankAccountRequired: boolean;
  securitiesDepositAccountRequired: boolean;
  showLoading: boolean;
  error?: AnyServerError;
} => {
  //hooks
  const { investmentId, resourceId: invitationId, loading, finalize } = useContext(WizardContext);

  const { error: apiError, loading: apiLoading, makeAuthenticatedApi, withApi } = useApiCall();

  const { investor: me, error: meError } = useInvestorMe();

  const {
    token: tokenInvitation,
    error: invitationError,
    loading: loadingInvitation,
    invitation,
  } = useInvestmentInvitation(invitationId);
  const { token: tokenInvestment } = useInvestment(investmentId);

  const token = invitation ? tokenInvitation : tokenInvestment;

  const showLoading = !token || loading || loadingInvitation || apiLoading;
  const error = invitationError || apiError || meError;

  const bankAccountRequired = useMemo(
    () => token?.bankAccountRequired && !me?.person.bankAccount,
    [me?.person.bankAccount, token?.bankAccountRequired],
  );
  const securitiesDepositAccountRequired = useMemo(
    () => token?.securitiesDepositAccountRequired && !me?.person.securitiesDepositAccount,
    [me?.person.securitiesDepositAccount, token?.id],
  );

  const investmentsApi: InvestmentsApi = useMemo(() => makeAuthenticatedApi(InvestmentsApi), [makeAuthenticatedApi]);

  //functions
  const onSubmit = useCallback(
    (values: AdditionalInformationFields) => {
      withApi(async () => {
        await investmentsApi.investmentsAdditionalInformationCreate({
          id: investmentId,
          additionalInformationUpdateRequest: {
            ...{
              exemptionOrderAmount: values?.exemptionOrderAmount
                ? toMoney(values?.exemptionOrderAmount, CurrencyEnum.EUR)
                : toMoney(0, CurrencyEnum.EUR),
            },
            investorData: {
              bankAccount:
                bankAccountRequired && values.bankIban.length > 0 && values.bankAccountHolder.length > 0
                  ? {
                      iban: values.bankIban,
                      accountHolder: values.bankAccountHolder,
                    }
                  : undefined,
              securitiesDepositAccount: securitiesDepositAccountRequired
                ? {
                    bic: values.bic,
                    accountHolder: values.securitiesDepositAccountHolder,
                    accountNumber: values.securitiesDepositAccountHolder,
                  }
                : undefined,
            },
          },
        });

        finalize();
      });
    },
    [investmentsApi, investmentId, withApi, finalize, bankAccountRequired],
  );

  return useMemo(
    () => ({
      bankAccountRequired: bankAccountRequired || false,
      securitiesDepositAccountRequired: securitiesDepositAccountRequired || false,
      showLoading,
      error,
      onSubmit,
    }),
    [
      bankAccountRequired,
      securitiesDepositAccountRequired,
      true,
      onSubmit,
      investmentId,
      invitationId,
      showLoading,
      error,
    ],
  );
};

export default useAdditionalInformation;
